import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { services } from "@/data/services";

const models = [
  {
    title: "Dedicated Team",
    desc: "A committed, cross-functional squad that operates as an extension of your organization for long-term initiatives.",
  },
  {
    title: "Project-Based",
    desc: "Fixed-scope, outcome-based delivery with a clear timeline, milestones, and accountable ownership.",
  },
  {
    title: "Staff Augmentation",
    desc: "Hand-picked engineers, QA, and specialists embedded into your existing teams to accelerate delivery.",
  },
];

export default function ServicesPage() {
  return (
    <>
      <Seo
        title="Services — Stantive Soft Solutions"
        description="Quality Engineering, AI Engineering, Salesforce and ERP Implementation, Custom Development, and Cloud Engineering services."
        path="/services"
      />
      <PageHero
        eyebrow="Our services"
        image="/images/hero-services.jpg"
        imagePosition="center"
        title="Deep expertise across the full software lifecycle."
        description="Five Zucitech-aligned practices, one accountable partner. Each engagement is staffed with senior engineers and shaped by proven delivery processes."
        actions={
          <>
            <Button
              asChild
              size="lg"
              className="bg-gradient-hero text-primary-foreground shadow-elegant hover:opacity-90"
            >
              <Link to="/contact">
                Get a Quote <ArrowRight size={16} className="ml-1" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-ink-foreground/25 bg-ink/25 text-ink-foreground backdrop-blur hover:bg-ink-foreground/10"
            >
              <a href="#services-grid">View Practices</a>
            </Button>
          </>
        }
        stats={[
          { value: "5", label: "Practices" },
          { value: "3", label: "Models" },
          { value: "24/7", label: "Support" },
          { value: "Global", label: "Delivery" },
        ]}
      />

      <section id="services-grid" className="scroll-mt-24 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 80}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:bg-ink hover:shadow-elegant"
                >
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary transition-colors group-hover:text-primary-glow">
                    0{i + 1}
                  </span>
                  <div className="mt-4 grid h-14 w-14 place-items-center rounded-2xl bg-gradient-hero text-primary-foreground shadow-elegant transition-transform duration-300 group-hover:scale-105">
                    <s.icon size={26} />
                  </div>
                  <h2 className="mt-5 font-display text-xl font-bold transition-colors group-hover:text-ink-foreground">
                    {s.title}
                  </h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground transition-colors group-hover:text-ink-muted">
                    {s.shortDescription}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-all group-hover:gap-2.5 group-hover:text-primary-glow">
                    Explore {s.title.split(" ")[0]}
                    <ArrowRight size={14} />
                  </span>
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-hero opacity-0 transition-opacity group-hover:opacity-100" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Engagement models
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Flexible ways to work together
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {models.map((m, i) => (
              <Reveal key={m.title} delay={i * 80}>
                <div className="group h-full rounded-2xl border border-border bg-card p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:bg-ink hover:shadow-elegant">
                  <div className="text-sm font-semibold text-primary transition-colors group-hover:text-primary-glow">
                    Model 0{i + 1}
                  </div>
                  <h3 className="mt-2 font-display text-xl font-bold transition-colors group-hover:text-ink-foreground">
                    {m.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground transition-colors group-hover:text-ink-muted">
                    {m.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
